import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  jsonLd?: object | object[];
}

const SITE_URL = 'https://bharat-consultancy.com';
const JSONLD_SCRIPT_ID = 'seo-jsonld';

/**
 * Manages <title>, meta description, OG tags, canonical URL, and JSON-LD
 * schema imperatively via document head mutation. Avoids pulling in
 * react-helmet(-async) as a dependency for what's a small, well-bounded
 * need — and matters for the prerender script, which runs after React
 * has painted, then reads document.title/meta directly (see
 * scripts/prerender.mjs) rather than relying on a library's SSR API.
 */
export function SEOHead({ title, description, canonicalPath, ogImage, jsonLd }: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    setMetaTag('description', description);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', 'website', true);
    if (ogImage) setMetaTag('og:image', ogImage, true);

    const canonicalHref = `${SITE_URL}${canonicalPath ?? window.location.pathname}`;
    setCanonical(canonicalHref);
    setMetaTag('og:url', canonicalHref, true);

    if (jsonLd) {
      let script = document.getElementById(JSONLD_SCRIPT_ID) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = JSONLD_SCRIPT_ID;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      // Intentionally not removing tags on unmount — the next page's
      // SEOHead call overwrites them on its own mount. Removing here
      // would create a flash of no-meta between route transitions.
    };
  }, [title, description, canonicalPath, ogImage, jsonLd]);

  return null;
}

function setMetaTag(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}
